import type {
  IFocalPoint,
  IStyleSpecCore,
} from '@/interfaces/IStorefrontStyle';
import type { IDesignDraft } from './design-draft';

/* ===== Preview protocol (CON-11): types and constants ===== */

export const SF_PREVIEW_QUERY_KEY = 'sfPreview';
export const SF_PREVIEW_PROTOCOL_VERSION = 1;

/** Storefront → admin, once after the storefront mounts in preview mode. Carries no data. */
export interface ISfReadyMessage {
  type: 'sf:ready';
  v: 1;
}

/** Admin → storefront, on every `sf:ready` and on every draft change (debounced 120 ms). */
export interface ISfPreviewMessage {
  type: 'sf:preview';
  v: 1;
  style: IStyleSpecCore;
  /** Draft focal point of the banner; null = use the saved `bannerMeta` focal. */
  bannerFocal: IFocalPoint | null;
  /** «Sin fotos» view: hide dish photos and the «Lo más pedido» / «Descuentos» strips. */
  hidePhotos: boolean;
}

/* ===== Builders and guards (ADM-2) ===== */

export const PREVIEW_READY_TIMEOUT_MS = 15000;
export const PREVIEW_DEBOUNCE_MS = 120;

/** The iframe address: the real store in preview mode (D-ADM-2). */
export function previewUrl(website: string, stringId: string): string {
  return `${website}/${encodeURIComponent(stringId)}?${SF_PREVIEW_QUERY_KEY}=1`;
}

/** The only origin the admin posts to and accepts `sf:ready` from. */
export function previewOrigin(website: string): string {
  return new URL(website).origin;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isReadyMessage(data: unknown): data is ISfReadyMessage {
  return isRecord(data) && data.type === 'sf:ready' && data.v === 1;
}

/**
 * The admin's half of the handshake (D-ADM-2): a ready message counts only
 * when it comes from the preview iframe's window and the storefront origin.
 * Plain parameters (a `MessageEvent` fits the first one) keep it testable
 * without a DOM.
 */
export function acceptsReadyEvent(
  event: { origin: string; source: unknown; data: unknown },
  expectedOrigin: string,
  frameWindow: unknown
): boolean {
  return (
    frameWindow !== null &&
    frameWindow !== undefined &&
    event.source === frameWindow &&
    event.origin === expectedOrigin &&
    isReadyMessage(event.data)
  );
}

/** A fresh plain object (never a reactive draft) for `postMessage`. */
export function buildPreviewMessage(
  draft: IDesignDraft,
  hidePhotos: boolean
): ISfPreviewMessage {
  return {
    type: 'sf:preview',
    v: 1,
    style: { preset: draft.preset, brand: draft.brand, mode: draft.mode },
    bannerFocal: draft.bannerFocal
      ? { x: draft.bannerFocal.x, y: draft.bannerFocal.y }
      : null,
    hidePhotos,
  };
}
